import { AccountTypeEnum, AdministratorPermissions, PermissionsEnum } from 'mbs-ui-shared@models';
import { ShowChatEnum } from 'mbs-ui-shared@models/ProviderData';

export type AdministratorPermissionsContext = {
  accountType: AccountTypeEnum;
  can: (permission: PermissionsEnum) => boolean;
  chat: ShowChatEnum;
};

export const AdministratorPermissionChildren: Partial<Record<PermissionsEnum, PermissionsEnum[]>> = {
  [PermissionsEnum.Dashboard]: [PermissionsEnum.CustomizeDashboard],
  [PermissionsEnum.Licenses]: [PermissionsEnum.LicenseBuy, PermissionsEnum.ActivateLicenseKey, PermissionsEnum.LicenseUsageHistory],
  [PermissionsEnum.RemoteManagement]: [
    PermissionsEnum.RmmPerAdminLicenseExists,
    PermissionsEnum.QuickRestore,
    PermissionsEnum.AgentlessRestore
  ],
  [PermissionsEnum.ShowRemoteConnection]: [PermissionsEnum.DirectConnectAccess],
  [PermissionsEnum.Downloads]: [PermissionsEnum.Rebranding],
  [PermissionsEnum.Users]: [
    PermissionsEnum.UsersCreateEdit,
    PermissionsEnum.AddOrRemoveDestinationForUser,
    PermissionsEnum.UsersDelete
    // PermissionsEnum.ManageStorageLimitsForUser
  ],
  [PermissionsEnum.Storage]: [PermissionsEnum.ManageImmutability],
  [PermissionsEnum.AssignTags]: [PermissionsEnum.ManageTags],
  [PermissionsEnum.ManageCompanies]: [
    PermissionsEnum.AddOrRemoveDestinationForCompany
    // PermissionsEnum.ManageStorageLimitsForCompany
  ],
  [PermissionsEnum.HelpMarketing]: [PermissionsEnum.EnableSupportChat]
};

export const AdministratorChildPermissions: PermissionsEnum[] = Object.values(AdministratorPermissionChildren).flat();

export function isAllowedAdministratorPermission(permission: PermissionsEnum, can: AdministratorPermissionsContext['can']): boolean {
  const dependencies = AdministratorPermissions[permission];

  if (!dependencies) return false;

  const groups = (Array.isArray(dependencies[0]) ? dependencies : [dependencies]) as PermissionsEnum[][];

  return groups.some((group) => group.every((dependency) => can(dependency)));
}

export function isAllowedAdministratorChildPermission(permission: PermissionsEnum, context: AdministratorPermissionsContext): boolean {
  if (!isAllowedAdministratorPermission(permission, context.can)) return false;

  // eslint-disable-next-line sonarjs/no-small-switch
  switch (permission) {
    case PermissionsEnum.EnableSupportChat:
      return context.accountType === AccountTypeEnum.Internal && context.chat !== ShowChatEnum.Hide;
    default:
      return true;
  }
}

export function allAdministratorPermissions(context: AdministratorPermissionsContext): PermissionsEnum[] {
  const parents = (Object.keys(AdministratorPermissions) as PermissionsEnum[]).filter(
    (permission) =>
      permission !== PermissionsEnum.UseOldConnectSettings &&
      !AdministratorChildPermissions.includes(permission) &&
      isAllowedAdministratorPermission(permission, context.can)
  );
  const children = parents.flatMap((parent) =>
    (AdministratorPermissionChildren[parent] ?? []).filter((child) => isAllowedAdministratorChildPermission(child, context))
  );

  return [PermissionsEnum.Backup, ...parents, ...children];
}
